import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import axios from 'axios';
import dayjs from 'dayjs';

import {
  STATUS_COLUMNS,
  TYPE_OPTIONS,
  PRIORITY_OPTIONS,
  typeMeta,
  priorityMeta,
  statusLabel,
} from './ticketConstants';
import {
  getDevTicket,
  updateDevTicket,
  deleteDevTicket,
  updateDevTicketStatus,
  addDevTicketComment,
  addDevTicketImage,
} from '../../collections/devTickets';

const IMAGE_UPLOAD_URL = 'https://api.maysfarm.vn/api/v1/images';

const uploadImage = async (file) => {
  const data = new FormData();
  data.append('file', file);
  const token = localStorage.getItem('token');
  const res = await axios.request({
    method: 'POST',
    url: IMAGE_UPLOAD_URL,
    headers: { Authorization: `Bearer ${token}` },
    data,
  });
  return res.data.data;
};

const TicketDetailDialog = ({
  open,
  ticketId,
  onClose,
  onChanged,
  onDeleted,
  isAdmin,
  currentAccountId,
}) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editType, setEditType] = useState('bug');
  const [editPriority, setEditPriority] = useState('medium');
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchTicket = async () => {
    if (!ticketId) return;
    setLoading(true);
    setError('');
    try {
      const res = await getDevTicket(ticketId);
      setTicket(res.data);
    } catch (err) {
      console.error(err);
      setError('Không tải được yêu cầu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && ticketId) {
      fetchTicket();
      setEditing(false);
      setNewComment('');
    }
  }, [open, ticketId]);

  const startEdit = () => {
    if (!ticket) return;
    setEditTitle(ticket.title);
    setEditDescription(ticket.description);
    setEditType(ticket.type);
    setEditPriority(ticket.priority);
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    try {
      const res = await updateDevTicket(ticket.id, {
        title: editTitle.trim(),
        description: editDescription.trim(),
        type: editType,
        priority: editPriority,
      });
      setTicket(res.data);
      setEditing(false);
      onChanged && onChanged(res.data);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || 'Cập nhật yêu cầu thất bại.');
    }
  };

  const handleStatusChange = async (event) => {
    const newStatus = event.target.value;
    try {
      const res = await updateDevTicketStatus(ticket.id, newStatus);
      setTicket(res.data);
      onChanged && onChanged(res.data);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || 'Đổi trạng thái thất bại.');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Xóa yêu cầu này? Hành động không thể hoàn tác.')) return;
    try {
      await deleteDevTicket(ticket.id);
      onDeleted && onDeleted(ticket.id);
      onClose();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || 'Xóa yêu cầu thất bại.');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await addDevTicketComment(ticket.id, newComment.trim());
      setNewComment('');
      await fetchTicket();
      onChanged && onChanged({ ...ticket, comment_count: (ticket.comment_count || 0) + 1 });
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || 'Gửi bình luận thất bại.');
    } finally {
      setPosting(false);
    }
  };

  const handleAddImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      if (url) {
        await addDevTicketImage(ticket.id, url);
        await fetchTicket();
        onChanged && onChanged({ ...ticket, image_count: (ticket.image_count || 0) + 1 });
      }
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || 'Đính kèm ảnh thất bại.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const canEditFields = ticket && (isAdmin || ticket.created_by === currentAccountId);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        {ticket ? `#${ticket.id} ${ticket.title}` : 'Đang tải…'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {ticket && !loading && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip
                size="small"
                label={`${typeMeta(ticket.type).emoji} ${typeMeta(ticket.type).label}`}
              />
              <Chip
                size="small"
                label={priorityMeta(ticket.priority).label}
                color={priorityMeta(ticket.priority).color}
                variant="outlined"
              />
              {isAdmin ? (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="status-select-label">Trạng thái</InputLabel>
                  <Select
                    labelId="status-select-label"
                    label="Trạng thái"
                    value={ticket.status}
                    onChange={handleStatusChange}
                  >
                    {STATUS_COLUMNS.map((c) => (
                      <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Chip size="small" label={statusLabel(ticket.status)} color="info" />
              )}
              <Box flexGrow={1} />
              {canEditFields && !editing && (
                <IconButton size="small" onClick={startEdit} aria-label="edit">
                  <EditOutlinedIcon />
                </IconButton>
              )}
              {isAdmin && (
                <IconButton size="small" onClick={handleDelete} aria-label="delete">
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            </Stack>

            {editing ? (
              <Stack spacing={2}>
                <TextField
                  label="Tiêu đề"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Mô tả"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                />
                <Stack direction="row" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="edit-type-label">Loại</InputLabel>
                    <Select
                      labelId="edit-type-label"
                      label="Loại"
                      value={editType}
                      onChange={(e) => setEditType(e.target.value)}
                    >
                      {TYPE_OPTIONS.map((t) => (
                        <MenuItem key={t.key} value={t.key}>
                          {t.emoji} {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="edit-priority-label">Ưu tiên</InputLabel>
                    <Select
                      labelId="edit-priority-label"
                      label="Ưu tiên"
                      value={editPriority}
                      onChange={(e) => setEditPriority(e.target.value)}
                    >
                      {PRIORITY_OPTIONS.map((p) => (
                        <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" size="small" onClick={handleSaveEdit}>Lưu</Button>
                  <Button size="small" onClick={() => setEditing(false)}>Hủy</Button>
                </Stack>
              </Stack>
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Người báo: {ticket.creator_name} • {ticket.created_at ? dayjs(ticket.created_at).format('DD/MM/YYYY HH:mm') : ''}
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                  {ticket.description}
                </Typography>
              </Box>
            )}

            <Divider />

            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Typography variant="subtitle2">Ảnh chụp màn hình ({ticket.images?.length || 0})</Typography>
                <Box flexGrow={1} />
                {canEditFields && (
                  <Button
                    size="small"
                    variant="outlined"
                    component="label"
                    disabled={uploading}
                  >
                    {uploading ? 'Đang tải lên…' : 'Thêm ảnh'}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={handleAddImage}
                    />
                  </Button>
                )}
              </Stack>
              {ticket.images && ticket.images.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {ticket.images.map((img) => (
                    <a key={img.id} href={img.image_url} target="_blank" rel="noreferrer">
                      <img
                        src={img.image_url}
                        alt={`ticket-${ticket.id}-${img.id}`}
                        style={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 4,
                          border: '1px solid #eee',
                        }}
                      />
                    </a>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">Chưa có ảnh.</Typography>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" mb={1}>
                Bình luận ({ticket.comments?.length || 0})
              </Typography>
              <Stack spacing={1.5} mb={2}>
                {(ticket.comments || []).map((c) => (
                  <Box key={c.id} sx={{ borderLeft: 2, borderColor: 'grey.300', pl: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {c.account_name} • {c.created_at ? dayjs(c.created_at).format('DD/MM/YYYY HH:mm') : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {c.body}
                    </Typography>
                  </Box>
                ))}
                {(!ticket.comments || ticket.comments.length === 0) && (
                  <Typography variant="body2" color="text.secondary">Chưa có bình luận.</Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Thêm bình luận…"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  multiline
                  maxRows={4}
                />
                <Button
                  variant="contained"
                  onClick={handleAddComment}
                  disabled={posting || !newComment.trim()}
                >
                  Gửi
                </Button>
              </Stack>
            </Box>

            {error && <Typography color="error" variant="body2">{error}</Typography>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketDetailDialog;
